/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './ContractBuilderGroup.styles';
import { useIntl } from 'react-intl';
import { Fragment, useRef } from 'react';
import IconButton from '~components/_common/IconButton/IconButton';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import { useGroupDropTarget } from '../ContractBuilderDnD/useGroupDropTarget';
import { useDraggableGroup } from '../ContractBuilderDnD/useDraggableGroup';
import { UniversalDropSpacer } from '~components/molecules/Tabs/TenderTabs/components/ContractAndOffers/ContractBuilder/ContractBuilderDnD/UniversalDropSpacer';
import { Button, Tooltip } from 'antd';

interface ContractBuilderGroupProps {
  groupId: string;
  groupTitle: string;
  cards?: any[];
  groupIndex: number;
  onRightClick?: (e: React.MouseEvent, groupIndex: number) => void;
  onItemClick?: (item: DataContextMenuItemType, groupIndex: number) => void;
  renderCard?: (
    card: any,
    index: number,
    groupIndex: number
  ) => React.ReactNode;
  dndIndex?: number;
  onDropGroup?: (from: string, to: string) => void;
  isDragging: boolean;
  setDraggingGroupId: (id: string | null) => void;
  onReorderCards?: (groupIndex: number, newCards: any[]) => void;
  onDropCardToGroup?: (
    fromCardId: string,
    groupIndex: number,
    toCardIndex: number
  ) => void;
  draggingCardId?: string | null;
  isOnMyTender: boolean;
  tenderClosed: boolean;
  isDeleted?: boolean;
  contractDeleted?: boolean;
}

const ContractBuilderGroup: React.FC<ContractBuilderGroupProps> = ({
  groupId,
  groupTitle,
  cards,
  groupIndex,
  onRightClick,
  onItemClick,
  renderCard,
  dndIndex,
  onDropGroup,
  isDragging,
  setDraggingGroupId,
  onReorderCards,
  onDropCardToGroup,
  draggingCardId,
  isOnMyTender,
  tenderClosed,
  isDeleted,
  contractDeleted,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const groupRef = useRef<HTMLDivElement | null>(null);

  useDraggableGroup(groupRef, groupId, {
    onDragStart: () => {
      if (!isOnMyTender) setDraggingGroupId(groupId);
    },
    onDrop: () => {
      if (!isOnMyTender) setDraggingGroupId(null);
    },
    disabled: isOnMyTender || tenderClosed || !!isDeleted || !!contractDeleted,
  });

  useGroupDropTarget(
    groupRef,
    groupId,
    onDropGroup ||
      (() => {
        //
      })
  );

  const dragDropTooltipTitle =
    !isOnMyTender &&
    intl.formatMessage({
      id: 'drag-and-drop-to-reorder',
    });

  return (
    <BaseRightClickOverlay
      items={
        isOnMyTender
          ? []
          : (isDeleted
              ? dataContextMenu.deletedContractGroup
              : dataContextMenu.contractGroup
            ).map((item) => ({
              ...item,
              disabled: !isOnMyTender
                ? tenderClosed || contractDeleted
                  ? true
                  : item.disabled
                : item.disabled,
            }))
      }
      onItemClick={(item) => onItemClick?.(item, groupIndex)}
      onOverlayClose={() => {
        //
      }}>
      <div
        ref={groupRef}
        css={[
          styles.groupWrapper,
          isDragging && styles.groupDraggingPlaceholder,
          isDeleted && {
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            opacity: 0.88,
          },
        ]}
        onContextMenu={(e) => {
          onRightClick?.(e, groupIndex);
        }}
        data-dnd-type="group"
        data-dnd-id={groupId}>
        <div css={styles.groupHeader}>
          <div css={{ display: 'table', width: 'auto' }}>
            <Tooltip
              title={dragDropTooltipTitle}
              placement="top"
              mouseEnterDelay={0.5}
              trigger={['hover']}>
              <div
                css={[
                  styles.groupTitle,
                  { display: 'inline-block', width: 'auto' },
                ]}>
                {groupTitle}
              </div>
            </Tooltip>
          </div>
          {!isOnMyTender && (
            <div css={styles.groupOption} style={{ display: 'flex' }}>
              <Button
                type="text"
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                disabled={tenderClosed || !!isDeleted || !!contractDeleted}
                onClick={() => {
                  if (tenderClosed || isDeleted || contractDeleted) return;

                  onItemClick?.(
                    {
                      key: 1,
                      icon: 'square-plus',
                      iconStyle: 'far',
                      name: 'add-voucher-to-group',
                      modal: 'add-card',
                      minPermissionLevel: 0,
                    },
                    groupIndex
                  );
                }}>
                <IconButton
                  color={
                    !isOnMyTender &&
                    (tenderClosed || isDeleted || contractDeleted)
                      ? 'grey'
                      : 'blue'
                  }
                  backgroundOff={true}
                  icon={['far', 'square-plus']}
                  customStyle={{ fontSize: '22px' }}
                />
                {intl.formatMessage({ id: 'add-voucher-to-group' })}
              </Button>
              <IconButton
                icon={['far', 'ellipsis-v']}
                widthTooltip={intl.formatMessage({ id: 'settings' })}
                customStyle={{
                  cursor:
                    !isOnMyTender &&
                    (tenderClosed || isDeleted || contractDeleted)
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    !isOnMyTender &&
                    (tenderClosed || isDeleted || contractDeleted)
                      ? 0.5
                      : 1,
                }}
                click={(e) => {
                  onRightClick?.(e, groupIndex);
                  const fakeEvent = new MouseEvent('contextmenu', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                    button: 2,
                    buttons: 2,
                    clientX: e.clientX,
                    clientY: e.clientY,
                  });
                  e.currentTarget.dispatchEvent(fakeEvent);
                }}
              />
            </div>
          )}
        </div>

        <div
          css={styles.groupCenterWrapper}
          onContextMenu={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}>
          {cards && cards.length > 0
            ? cards.map((card, cardIndex) => {
                const isDraggingSelf = draggingCardId === card.id.toString();
                const draggingCardIndex = cards.findIndex(
                  (c) => c.id.toString() === draggingCardId
                );
                const isSpacerAfterDragged =
                  draggingCardIndex !== -1 &&
                  draggingCardIndex === cardIndex - 1;

                return (
                  <Fragment key={card.id}>
                    {draggingCardId &&
                      !isDraggingSelf &&
                      !isSpacerAfterDragged &&
                      !isDeleted &&
                      !contractDeleted && (
                        <UniversalDropSpacer
                          index={cardIndex}
                          groupIndex={groupIndex}
                          onDropCard={(fromId, toIndex, gIndex) => {
                            if (!onDropCardToGroup) return;
                            if (isDeleted || contractDeleted) return;

                            // Adjust index for the case when dragging from top to bottom
                            const draggingCardIndex = cards.findIndex(
                              (c) => c.id.toString() === draggingCardId
                            );
                            let adjustedIndex = toIndex;
                            if (
                              draggingCardIndex !== -1 &&
                              draggingCardIndex < toIndex
                            ) {
                              adjustedIndex = toIndex - 1;
                            }
                            onDropCardToGroup(fromId, gIndex, adjustedIndex);
                          }}
                          onDropGroup={() => {
                            //
                          }}
                          active
                          draggingCardId={draggingCardId}
                        />
                      )}
                    {renderCard?.(card, cardIndex, groupIndex)}
                  </Fragment>
                );
              })
            : intl.formatMessage({ id: 'no-cards-available' })}

          {/* Final spacer at the end of the group */}
          {draggingCardId && !isDeleted && !contractDeleted && (
            <UniversalDropSpacer
              index={cards?.length || 0}
              groupIndex={groupIndex}
              onDropCard={(fromId, toIndex, gIndex) => {
                if (!onDropCardToGroup) return;
                if (isDeleted || contractDeleted) return;

                // Adjust index for the case when dragging from top to bottom
                const draggingCardIndex = cards.findIndex(
                  (c) => c.id.toString() === draggingCardId
                );
                let adjustedIndex = toIndex;
                if (draggingCardIndex !== -1 && draggingCardIndex < toIndex) {
                  adjustedIndex = toIndex - 1;
                }
                onDropCardToGroup(fromId, gIndex, adjustedIndex);
              }}
              onDropGroup={() => {
                //
              }}
              active
              draggingCardId={draggingCardId}
            />
          )}
        </div>
      </div>
    </BaseRightClickOverlay>
  );
};

export default ContractBuilderGroup;
